import React from 'react';
import { observer } from 'mobx-react-lite';
import { runInAction, toJS } from 'mobx';
import { useRootStore } from 'store/rootStore';
import CartItemProductLineHeader from 'components/proposal-v2/CartItemProductLineHeader';
import clsx from 'clsx';
import { ProposalCard, RenewalCard } from '@insurancemenu/imenu-components-lib';
import CartItemSelectBar from 'components/proposal-v2/CartItemSelectBar';
import DynamicFormModalDEPRECATED from 'components/common/modals/DynamicFormModal_DEPRECATED';
import RenewalItemSelectBar from 'components/proposal-v2/RenewalItemSelectBar';
import { EnrollmentType } from 'types/enrollmentService';
import { useHistory, useParams } from 'react-router-dom';
import { ProductLineDelegateContext, useProductLineDelegate } from 'store/pages/proposal/productLineContext';
import ProductLineDelegate from 'store/modules/proposal/delegates/productLineDelegate';
import ManageTagModal from 'components/common/proposal/ManageTagModal';
import { CartItemDelegateContext, useCartItemDelegate } from 'store/pages/proposal/cartItemContext';
import CartItemDelegate from 'store/modules/proposal/delegates/cartItemDelegate';
import { getRequiredCrossSellProductLineId } from 'global/utils/crossSellHelper';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { ProposalCardWrapperStyled, createStyles, CrossSellPlanLabelStyled } from './styles';
import useManageTagModalStore from './useManageTagModalStore';

const UsualManageTagModal = observer(() => {
  const store = useManageTagModalStore();
  return <ManageTagModal store={store} />;
});

const CartItem = observer(() => {
  const { periodId } = useParams<{ periodId: string }>();
  const history = useHistory();

  const productLineDelegate = useProductLineDelegate();
  const cartItemDelegate = useCartItemDelegate();

  const {
    portfolioConfigModalStore: { openConfigurePlanModal },
    proposalItemsStore: {
      cart: { id: cartId, lastUpdateBrowsingCriteria },
      cartItems,
      isProposalEditable,
      isProposalConfirmed,
      setBrowsingCriteria,
      onViewPlanDetails,
    },
  } = useRootStore();

  const portfolio = cartItemDelegate.wholePortfolio;
  const crossSellProductLineId = getRequiredCrossSellProductLineId(portfolio.id);
  const crossSellProductLineLabel = crossSellProductLineId
    ? PRODUCT_LINES_CONTENT_MAP[crossSellProductLineId].label
    : undefined;

  async function configurePortfolio(): Promise<void> {
    await openConfigurePlanModal(
      cartItemDelegate.wholePortfolio,
      cartId,
      lastUpdateBrowsingCriteria.effectiveDate,
      cartItems,
    );
  }

  function viewPlanDetails(portfolioId: string): void {
    onViewPlanDetails(portfolioId, history.push);
  }

  function browsingChange(id: string, selectedBcIndex: number): void {
    setBrowsingCriteria(id, selectedBcIndex);
  }

  const openManageProductTagModal = (_: string, productId: string, tag: string): void => {
    cartItemDelegate.openManageTagModal({ productId, tag, periodId });
  };

  const classes = createStyles();

  return (
    <div key={`proposal_card_${cartItemDelegate.portfolioId}`} className={classes.cartItem}>
      {isProposalEditable && (
        <CartItemSelectBar
          portfolioId={cartItemDelegate.portfolioId}
          productLineId={productLineDelegate.productLineId}
        />
      )}
      <ProposalCardWrapperStyled
        $hasCrossSellPlan={!!crossSellProductLineId}
        $isAddedToCart={cartItemDelegate.wholeCartItem.confirmed}
        key={portfolio.id}
      >
        {crossSellProductLineLabel && (
          <CrossSellPlanLabelStyled>
            <span>Requires adding a {crossSellProductLineLabel} plan</span>
          </CrossSellPlanLabelStyled>
        )}
        <ProposalCard
          productLineAttributes={productLineDelegate.productLineAttributes.map((dateObj) => toJS(dateObj)).slice()}
          marketingTags={cartItemDelegate.marketingTags.map((dateObj) => toJS(dateObj)).slice()}
          portfolio={toJS(cartItemDelegate.wholePortfolio)}
          cartItem={toJS(cartItemDelegate.wholeCartItem)}
          isPlanConfigurable={cartItemDelegate.isPlanConfigurable}
          isProposalConfirmed={isProposalConfirmed}
          userRoles={cartItemDelegate.userRoles.map((dateObj) => toJS(dateObj)).slice()}
          onViewPlanDetails={viewPlanDetails}
          onConfigurePortfolio={configurePortfolio}
          onBrowsingChange={browsingChange}
          loading={cartItemDelegate.isPortfolioLoading}
          onManageProductTag={openManageProductTagModal}
        />
      </ProposalCardWrapperStyled>
    </div>
  );
});

const RenewalCardList = observer((): JSX.Element | null => {
  const {
    proposalItemsStore: { enrollmentPeriod, renewAsIsSelected },
    domainStoreV2,
  } = useRootStore();
  const { productLineId } = useProductLineDelegate();

  const classes = createStyles();

  const onDownload = (currentProductLineId: string): void => {
    enrollmentPeriod.installedProposal?.productLines
      .find((line) => line.id === currentProductLineId)
      ?.exhibitUrls?.map((exhibit) => window.open(exhibit, '_blank'));
  };

  // Don't show renewal portfolio card if the enrollment period is not in Renewal
  if (enrollmentPeriod.type !== EnrollmentType.RENEWAL) {
    return null;
  }

  const installedProposal = toJS(enrollmentPeriod.installedProposal);

  return (
    <>
      {installedProposal?.productLines
        .filter((productLineData) => productLineData.id === productLineId)
        .filter((productLineData) => productLineData.products.length > 0)
        .map((productLineData) => {
          return (
            <div className={classes.cartItem} key={`renewalItem${productLineData.imPortfolioId}`}>
              <RenewalItemSelectBar productLineId={productLineId} />
              <RenewalCard
                imPortfolioId={productLineData.imPortfolioId}
                carrierPortfolioName={productLineData.carrierPortfolioName}
                products={productLineData.products}
                onDownload={(): void => onDownload(productLineData.id)}
                carrier={{
                  logo: domainStoreV2.domainHeader.logo,
                  name: domainStoreV2.domainHeader.name,
                }}
                isSelected={renewAsIsSelected(productLineId)}
              />
            </div>
          );
        }) || null}
    </>
  );
});

const CartItemsCollection = observer(() => {
  const productLineDelegate = useProductLineDelegate();
  const {
    proposalItemsStore: { isRenewalProductLine },
    proposalPageStore,
  } = useRootStore();
  const classes = createStyles();

  return (
    <div className={classes.cartItemsContent}>
      <div
        className={clsx(
          classes.cartItemContent,
          productLineDelegate.isDontProvide && classes.cartItemContentInactiveBackdrop,
        )}
      >
        <>
          <RenewalCardList />
          {productLineDelegate.portfolioToCartItems.length > 0 &&
            isRenewalProductLine(productLineDelegate.productLineId) && (
              <div className={classes.alternativeText}>
                <span>Alternative Plans</span>
              </div>
            )}
        </>
      </div>
      {productLineDelegate.portfolioToCartItems.map((item) => (
        <CartItemDelegateContext.Provider
          value={
            new CartItemDelegate({
              pageStore: proposalPageStore,
              productLineDelegate,
              portfolioToCartItem: item,
            })
          }
          key={item.portfolio.id}
        >
          <div
            className={clsx(
              classes.cartItemContent,
              productLineDelegate.isDontProvide && classes.cartItemContentInactiveBackdrop,
            )}
          >
            <CartItem />
          </div>
          <UsualManageTagModal />
        </CartItemDelegateContext.Provider>
      ))}
    </div>
  );
});

const ProposalPlans = observer((): JSX.Element => {
  // Mobx store
  const {
    proposalItemsStore: {
      portfoliosByProductLine,
      isProposalEditable,
      cart: { id: cartId, lastUpdateBrowsingCriteria },
      cartItems,
      setLoadingForPortfolio,
      getPortfolioById,
      updateCartItem,
    },
    portfolioConfigModalStore: {
      showConfigurePlanModal,
      dynamicForm,
      loading,
      form,
      portfolioId: configurePortfolioId,
      saveAnswers,
      closeConfigurePlanModal,
      openConfigurePlanModal,
    },
    proposalPageStore,
  } = useRootStore();
  const convertedPortfolioItemsByProductLines = portfoliosByProductLine.map((dateObj) => toJS(dateObj)).slice();
  const productLines = convertedPortfolioItemsByProductLines.map((item) => item.productLineId);

  async function configurePortfolio(portfolioId: string): Promise<void> {
    const portfolio = getPortfolioById(portfolioId);
    if (portfolio) {
      await openConfigurePlanModal(portfolio, cartId, lastUpdateBrowsingCriteria.effectiveDate, cartItems);
    }
  }

  const savePortfolioConfigModalChanges = async (): Promise<void> => {
    setLoadingForPortfolio(configurePortfolioId, true);
    closeConfigurePlanModal();
    const updatedCard = await saveAnswers().catch(async (): Promise<null> => {
      setLoadingForPortfolio(configurePortfolioId, false);
      await configurePortfolio(configurePortfolioId);
      return null;
    });
    await runInAction(async () => {
      if (updatedCard?.data?.addPortfolioConfigToCart) {
        await updateCartItem(updatedCard.data?.addPortfolioConfigToCart, configurePortfolioId);
      }
    });
  };

  const classes = createStyles();

  return (
    <>
      <div className={classes.proposalsList}>
        {productLines &&
          productLines.map((productLineId) => (
            <section className={classes.productLineSection} key={`proposal_card_section_${productLineId}`}>
              <ProductLineDelegateContext.Provider value={new ProductLineDelegate(proposalPageStore, productLineId)}>
                <CartItemProductLineHeader productLineId={productLineId} />
                <CartItemsCollection />
              </ProductLineDelegateContext.Provider>
            </section>
          ))}
      </div>
      <DynamicFormModalDEPRECATED
        show={showConfigurePlanModal}
        onClose={closeConfigurePlanModal}
        onAction={savePortfolioConfigModalChanges}
        dynamicForm={dynamicForm}
        form={form}
        loading={loading}
        isEditable={isProposalEditable}
        formStyles={classes.bcForm}
      />
    </>
  );
});

export default ProposalPlans;
